/**
 * User Journey Service
 * Tracks user progress through the FinList interaction flow
 */

import { db, auth } from '@/config/firebase';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

/**
 * Journey steps in order
 */
export const JOURNEY_STEPS = {
  SIGNED_UP: 'signed_up',
  VIEWED_DASHBOARD: 'viewed_dashboard',
  CREATED_SHOPPING_LIST: 'created_shopping_list',
  ADDED_ITEMS: 'added_items',
  MARKED_BOUGHT: 'marked_bought',
  ADDED_INCOME: 'added_income',
  VIEWED_INSIGHTS: 'viewed_insights',
  SET_GOAL: 'set_goal',
  COMPLETED: 'completed'
};

/**
 * Journey milestones with descriptions
 */
export const JOURNEY_MILESTONES = [
  {
    id: JOURNEY_STEPS.SIGNED_UP,
    title: '🎉 Welcome to FinList!',
    description: 'Your account is ready',
    order: 1
  },
  {
    id: JOURNEY_STEPS.VIEWED_DASHBOARD,
    title: '🏠 Dashboard Explored',
    description: 'You\'ve seen your financial overview',
    order: 2
  },
  {
    id: JOURNEY_STEPS.CREATED_SHOPPING_LIST,
    title: '🛒 First Shopping List',
    description: 'Created your first shopping list',
    order: 3
  },
  {
    id: JOURNEY_STEPS.ADDED_ITEMS,
    title: '📦 Items Added',
    description: 'Added items to your list',
    order: 4
  },
  {
    id: JOURNEY_STEPS.MARKED_BOUGHT,
    title: '✅ First Purchase',
    description: 'Marked items as bought (auto expense created)',
    order: 5
  },
  {
    id: JOURNEY_STEPS.ADDED_INCOME,
    title: '💰 Income Tracked',
    description: 'Logged your income',
    order: 6
  },
  {
    id: JOURNEY_STEPS.VIEWED_INSIGHTS,
    title: '📈 Insights Viewed',
    description: 'Checked your spending insights',
    order: 7
  },
  {
    id: JOURNEY_STEPS.SET_GOAL,
    title: '🎯 Goal Created',
    description: 'Set your first savings goal',
    order: 8
  },
  {
    id: JOURNEY_STEPS.COMPLETED,
    title: '🎊 Journey Complete!',
    description: 'You\'re a FinList pro now!',
    order: 9
  }
];

/**
 * Get user's journey progress
 */
export async function getUserJourney(userId = null) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  try {
    const journeyRef = doc(db, 'users', uid, 'settings', 'journey');
    const journeySnap = await getDoc(journeyRef);

    if (!journeySnap.exists()) {
      // Initialize journey for new user
      const initialJourney = {
        currentStep: JOURNEY_STEPS.SIGNED_UP,
        completedSteps: [JOURNEY_STEPS.SIGNED_UP],
        startedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
        totalSteps: Object.keys(JOURNEY_STEPS).length,
        completionPercentage: 11 // 1 out of 9 steps
      };
      
      await setDoc(journeyRef, initialJourney);
      return initialJourney;
    }

    return journeySnap.data();
  } catch (error) {
    console.error('Error getting user journey:', error);
    return null;
  }
}

/**
 * Mark journey step as completed
 */
export async function completeJourneyStep(step, userId = null) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  try {
    const journeyRef = doc(db, 'users', uid, 'settings', 'journey');
    const journeySnap = await getDoc(journeyRef);

    if (!journeySnap.exists()) {
      await getUserJourney(uid); // Initialize
    }

    const journey = journeySnap.exists() ? journeySnap.data() : {};
    const completedSteps = journey.completedSteps || [JOURNEY_STEPS.SIGNED_UP];

    // Don't add duplicate
    if (completedSteps.includes(step)) {
      return journey;
    }

    const newCompletedSteps = [...completedSteps, step];
    const totalSteps = Object.keys(JOURNEY_STEPS).length;
    const completionPercentage = Math.round((newCompletedSteps.length / totalSteps) * 100);

    // Determine current step (next incomplete step)
    const allSteps = Object.values(JOURNEY_STEPS);
    const nextStep = allSteps.find(s => !newCompletedSteps.includes(s)) || JOURNEY_STEPS.COMPLETED;

    const updates = {
      completedSteps: newCompletedSteps,
      currentStep: nextStep,
      completionPercentage,
      lastActivityAt: serverTimestamp()
    };

    // Mark as completed if all steps done
    if (completionPercentage === 100) {
      updates.completedAt = serverTimestamp();
    }

    await updateDoc(journeyRef, updates);

    console.log(`✅ Journey step completed: ${step} (${completionPercentage}%)`);

    return {
      ...journey,
      ...updates,
      stepJustCompleted: step
    };
  } catch (error) {
    console.error('Error completing journey step:', error);
    return null;
  }
}

/**
 * Get next step suggestion
 */
export async function getNextStepSuggestion(userId = null) {
  const journey = await getUserJourney(userId);
  if (!journey) return null;

  const currentStepIndex = JOURNEY_MILESTONES.findIndex(
    m => m.id === journey.currentStep
  );

  if (currentStepIndex === -1 || currentStepIndex >= JOURNEY_MILESTONES.length) {
    return null;
  }

  const nextMilestone = JOURNEY_MILESTONES[currentStepIndex];

  // Map steps to suggested actions
  const actionMap = {
    [JOURNEY_STEPS.VIEWED_DASHBOARD]: {
      action: 'View Dashboard',
      route: '/dashboard',
      icon: 'Home',
      color: '#006D77'
    },
    [JOURNEY_STEPS.CREATED_SHOPPING_LIST]: {
      action: 'Create Shopping List',
      route: '/shopping-lists',
      icon: 'ShoppingCart',
      color: '#06D6A0'
    },
    [JOURNEY_STEPS.ADDED_ITEMS]: {
      action: 'Add Items to List',
      route: '/shopping-lists',
      icon: 'Plus',
      color: '#06D6A0'
    },
    [JOURNEY_STEPS.MARKED_BOUGHT]: {
      action: 'Mark Items as Bought',
      route: '/shopping-lists',
      icon: 'Check',
      color: '#10b981'
    },
    [JOURNEY_STEPS.ADDED_INCOME]: {
      action: 'Add Income',
      route: '/dashboard',
      icon: 'TrendingUp',
      color: '#FFD60A'
    },
    [JOURNEY_STEPS.VIEWED_INSIGHTS]: {
      action: 'View Insights',
      route: '/insights',
      icon: 'BarChart',
      color: '#118AB2'
    },
    [JOURNEY_STEPS.SET_GOAL]: {
      action: 'Set Savings Goal',
      route: '/savings-goals',
      icon: 'Target',
      color: '#EF476F'
    },
    [JOURNEY_STEPS.COMPLETED]: {
      action: 'Explore Features',
      route: '/dashboard',
      icon: 'Sparkles',
      color: '#8b5cf6'
    }
  };

  return {
    ...nextMilestone,
    ...actionMap[journey.currentStep],
    progress: journey.completionPercentage
  };
}

/**
 * Check if user should see onboarding tour
 */
export async function shouldShowOnboarding(userId = null) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) return false;

  try {
    const journeyRef = doc(db, 'users', uid, 'settings', 'journey');
    const journeySnap = await getDoc(journeyRef);

    if (!journeySnap.exists()) {
      return true; // New user
    }

    const journey = journeySnap.data();
    
    // Show onboarding if:
    // 1. User hasn't dismissed it
    // 2. Journey is less than 50% complete
    return !journey.onboardingDismissed && journey.completionPercentage < 50;
  } catch (error) {
    console.error('Error checking onboarding:', error);
    return false;
  }
}

/**
 * Dismiss onboarding tour
 */
export async function dismissOnboarding(userId = null) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  try {
    const journeyRef = doc(db, 'users', uid, 'settings', 'journey');
    await updateDoc(journeyRef, {
      onboardingDismissed: true,
      dismissedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error dismissing onboarding:', error);
  }
}

/**
 * Reset user journey (for testing)
 */
export async function resetJourney(userId = null) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  try {
    const journeyRef = doc(db, 'users', uid, 'settings', 'journey');
    await setDoc(journeyRef, {
      currentStep: JOURNEY_STEPS.SIGNED_UP,
      completedSteps: [JOURNEY_STEPS.SIGNED_UP],
      startedAt: serverTimestamp(),
      lastActivityAt: serverTimestamp(),
      totalSteps: Object.keys(JOURNEY_STEPS).length,
      completionPercentage: 11,
      onboardingDismissed: false
    });

    console.log('✅ Journey reset successfully');
  } catch (error) {
    console.error('Error resetting journey:', error);
  }
}
